import { type FC } from "react";

import { StringParam, useQueryParams, withDefault } from "use-query-params";

import { useUserCurrency } from "@features/user/hooks/useUserCurrency";

import { useOnboardingStep } from "../onboarding-nav/use-onboarding-number";
import { Steps } from "../steps";

import { Step, type StepChildProps } from "./step";
import { StepContainer } from "./step-container";

export const Currency: FC<StepChildProps> = ({
  nextStep,
  previousStep,
  isLast,
}) => {
  const [_, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    step: withDefault(StringParam, ""),
    provider: withDefault(StringParam, ""),
  });
  const { stepDictionary } = useOnboardingStep();
  const { currentCurrencyCode, currentCurrency } = useUserCurrency();

  return (
    <StepContainer
      isLast={isLast}
      onMoveBackwardButtonClicked={() => {
        setSearchParams({
          step: Steps.BASIC_DETAILS,
        });
        previousStep && previousStep();
      }}
      title={`${stepDictionary["currency"].index}. Select Currency`}
      onMoveForwardButtonClicked={() => {
        setSearchParams({
          step: Steps.DOMAIN,
        });
        nextStep && nextStep();
      }}
      knowledgeBaseLink="https://docs.northbeam.io/docs/currency"
      knowledgeBaseText="Currency"
    >
      <Step
        nextStepButtonText={
          stepDictionary["domain"]
            ? `${stepDictionary["domain"].index}. Enter Domain Name`
            : `${stepDictionary["orders-api"].index}. Orders API`
        }
        nextStep={() => {
          setSearchParams({
            step: stepDictionary["domain"] ? Steps.DOMAIN : Steps.ORDERS_API,
          });
          nextStep && nextStep();
        }}
      >
        <div className="inline-flex flex-col gap-6 justify-start items-start px-10 pt-10 pb-20">
          <div className="self-stretch text-2xl font-semibold leading-8 text-slate-900">
            We automatically set the reporting currency to {currentCurrencyCode}{" "}
            ({currentCurrency?.symbol}); please contact Customer Service to
            change currency
          </div>
          <div className="self-stretch text-lg font-normal leading-normal text-slate-900">
            We normalize everything into a single currency even if you use
            different currencies in your ad accounts.
          </div>
          <div className="inline-flex gap-4 justify-start items-end">
            <div className="inline-flex flex-col gap-1 justify-end items-start w-44 h-14">
              <div className="self-stretch h-4 text-xs font-medium tracking-wide leading-none uppercase text-neutral-300">
                Currency
              </div>
              <div className="relative">
                <input
                  className="text-sm w-[174px] h-10 rounded-[4px] border border-gray-1 px-3 py-2.5 text-gray-0"
                  disabled
                  value={`${currentCurrency?.symbol} (${currentCurrencyCode})`}
                />
                <i
                  style={{ color: "#979797" }}
                  className="absolute fa-solid fa-lock top-[30%] right-3"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-start items-center pb-2">
              <div>
                <a
                  href="mailto:success@northbeam.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cosmic+0 text-sm font-normal underline leading-tight"
                >
                  Contact Customer Service
                </a>
                <span className="text-sm font-normal leading-tight text-slate-900">
                  {" "}
                  to change from {currentCurrencyCode} to another currency
                </span>
              </div>
            </div>
          </div>
        </div>
      </Step>
    </StepContainer>
  );
};
